import React, { FC, useState } from 'react';

import {
  Button,
  Card,
  CardContent,
  Typography,
  Box,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
} from '@mui/material';

import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import { BackflowJobItem } from '../../model';

interface SubmissionPdfCardProps {
  item: BackflowJobItem;
}

const SubmissionPdfCard: FC<SubmissionPdfCardProps> = (props) => {
  const { item } = props;
  const [modalOpen, setModalOpen] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const handleView = () => {
    if (item.water_authority_submission_pdf_url) {
      window.open(item.water_authority_submission_pdf_url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleCopyField = (value: string, fieldName: string) => {
    navigator.clipboard.writeText(value);
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const DetailRow: FC<{ label: string; value: string | undefined; fieldKey: string }> = ({
    label,
    value,
    fieldKey,
  }) => {
    const displayValue = value || 'N/A';
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 1.5,
          px: 2,
          borderRadius: 1,
          bgcolor: 'grey.50',
          '&:hover': { bgcolor: 'grey.100' },
          mb: 1,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase' }}>
            {label}
          </Typography>
          <Typography
            variant="body1"
            fontWeight={500}
            sx={{
              mt: 0.5,
              fontFamily: 'monospace',
              userSelect: 'all',
              cursor: 'text',
            }}
          >
            {displayValue}
          </Typography>
        </Box>
        {value && (
          <IconButton
            size="small"
            onClick={() => handleCopyField(value, fieldKey)}
            sx={{
              ml: 2,
              transition: 'all 0.2s',
              color: copiedField === fieldKey ? 'success.main' : 'action.active',
            }}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    );
  };

  return (
    <Card elevation={2} sx={{ height: '100%' }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Typography variant="h6" fontWeight="600" color="primary">
            {item.asset_no}
          </Typography>
          <Chip label={item.status} color="success" size="small" sx={{ textTransform: 'uppercase', fontWeight: 600 }} />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            <strong>Location:</strong>
          </Typography>
          <Typography variant="body1" gutterBottom>
            {item.location || 'N/A'}
          </Typography>

          <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 1 }}>
            <strong>Device Type:</strong>
          </Typography>
          <Typography variant="body1">{item.device_type || 'N/A'}</Typography>
        </Box>

        <Divider />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<OpenInNewIcon />}
            onClick={handleView}
            disabled={!item.water_authority_submission_pdf_url}
            fullWidth
            size="medium"
          >
            View Report
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<InfoIcon />}
            onClick={() => setModalOpen(true)}
            fullWidth
            size="medium"
          >
            View Job Details
          </Button>
        </Box>
      </CardContent>

      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 2,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Job Details - {item.asset_no}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              Click field values to select, use copy buttons to copy to clipboard
            </Typography>
          </Box>
          <IconButton
            onClick={() => setModalOpen(false)}
            sx={{
              color: 'white',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Grid container spacing={3}>
            {/* Work Order */}
            <Grid item xs={12} style={{ marginTop: 20 }}>
              <DetailRow label="Company Name" value={item.company_name} fieldKey="company_name" />
              <DetailRow label="Customer Address 1" value={item.location} fieldKey="address1" />
              <DetailRow label="Customer City" value={undefined} fieldKey="city" />
              <DetailRow label="Customer State" value={undefined} fieldKey="state" />
              <DetailRow label="Customer Zip" value={undefined} fieldKey="zip" />
              <DetailRow label="Customer Full Address" value={item.location} fieldKey="full_address" />
              <DetailRow label="Contact Name" value={undefined} fieldKey="contact_name" />
              <DetailRow label="Authority Account Number" value={undefined} fieldKey="authority_account_number" />
              <DetailRow label="Authority Service Number" value={undefined} fieldKey="authority_service_number" />
            </Grid>

            {/* Device */}
            <Grid item xs={12}>
              <Typography variant="h6" color="primary" fontWeight={600} gutterBottom sx={{ mb: 2 }}>
                Device
              </Typography>
              <DetailRow
                label="Backflow Manufacturer"
                value={item.backflow_log?.backflow_manufacturer || item.bfp_manufacturer_name || item.manufact}
                fieldKey="backflow_manufacturer"
              />
              <DetailRow
                label="Backflow Model Number"
                value={item.backflow_log?.backflow_model_number || item.model_no}
                fieldKey="backflow_model_number"
              />
              <DetailRow
                label="Backflow Serial Number"
                value={item.backflow_log?.backflow_serial_number || item.serial_no}
                fieldKey="backflow_serial_number"
              />
              <DetailRow label="Backflow Type Text" value={item.backflow_type} fieldKey="backflow_type_text" />
              <DetailRow label="Backflow Type Radio" value={item.backflow_type} fieldKey="backflow_type_radio" />
              <DetailRow label="Location" value={item.location} fieldKey="location" />
              <DetailRow
                label="Water Meter Manufacturer"
                value={item.wm_manufacturer_name}
                fieldKey="water_meter_manufacturer"
              />
              <DetailRow
                label="Water Meter Serial Number"
                value={item.water_meter_serial}
                fieldKey="water_meter_serial_number"
              />
            </Grid>

            {/* Test Result */}
            <Grid item xs={12}>
              <Typography variant="h6" color="primary" fontWeight={600} gutterBottom sx={{ mb: 2 }}>
                Test Result
              </Typography>
              <DetailRow label="Backflow Size" value={item.backflow_log?.backflow_size} fieldKey="backflow_size" />
              <DetailRow label="Client Comments" value={item.backflow_log?.client_comment} fieldKey="client_comment" />
              <DetailRow
                label="Line Pressure PSI"
                value={item.backflow_log?.line_pressure_psi?.toString()}
                fieldKey="line_pressure_psi"
              />
              <DetailRow label="Test Date" value={undefined} fieldKey="test_date" />
              <DetailRow label="Test Time" value={undefined} fieldKey="test_time" />
              <DetailRow
                label="Valve 1 Tight"
                value={item.backflow_log?.test1_valve_tight?.toString()}
                fieldKey="valve_1_tight"
              />
              <DetailRow
                label="Test 1 Gauge Value"
                value={item.backflow_log?.test1_gauge_value?.toString()}
                fieldKey="test1_gauge_value"
              />
              <DetailRow
                label="Valve 2 Tight"
                value={item.backflow_log?.test2_valve_tight?.toString()}
                fieldKey="valve_2_tight"
              />
              <DetailRow
                label="Test 2 Gauge Value"
                value={item.backflow_log?.test2_gauge_value?.toString()}
                fieldKey="test2_gauge_value"
              />
              <DetailRow
                label="Valve 3 Opened"
                value={item.backflow_log?.test3_valve_open?.toString()}
                fieldKey="test3_valve_open"
              />
              <DetailRow
                label="Test 3 Relief Valve Pressure"
                value={item.backflow_log?.test3_relief_valve_pressure?.toString()}
                fieldKey="test3_relief_valve_pressure"
              />
            </Grid>

            {/* Technician */}
            <Grid item xs={12}>
              <Typography variant="h6" color="primary" fontWeight={600} gutterBottom sx={{ mb: 2 }}>
                Technician
              </Typography>
              <DetailRow label="Certification Number" value={undefined} fieldKey="certification_number" />
              <DetailRow label="Cert. Calibration Date" value={undefined} fieldKey="calibration_date" />
              <DetailRow label="Technician Name" value={item.display_emp_id} fieldKey="tech_name" />
              <DetailRow label="Technician Signature" value={undefined} fieldKey="tech_signature" />
              <DetailRow label="Test Kit Make and Model" value={undefined} fieldKey="test_kit_make_and_model" />
            </Grid>

            {/* Company */}
            <Grid item xs={12}>
              <Typography variant="h6" color="primary" fontWeight={600} gutterBottom sx={{ mb: 2 }}>
                Company
              </Typography>
              <DetailRow label="TUDI Company Name" value={undefined} fieldKey="tudi_company_name" />
              <DetailRow label="TUDI Phone (Pgh)" value={undefined} fieldKey="tudi_phone_pgh" />
              <DetailRow label="TUDI Phone (Tampa)" value={undefined} fieldKey="tudi_phone_tampa" />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, bgcolor: 'grey.50' }}>
          <Button onClick={() => setModalOpen(false)} variant="contained" size="large">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default SubmissionPdfCard;
